import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { AttractionCategoryEnum } from '@routellers/types/dist/city-attraction/enum/attraction-category.enum';
import { AttractionFameEnum } from '@routellers/types/dist/city-attraction/enum/attraction-fame.enum';
import { CreatePartnerAttractionDto } from '@routellers/types/dist/partners-portal/create-partner-attraction.dto';
import {
    CreatePartnerCityQuestDto,
    UpdatePartnerCityQuestDto,
} from '@routellers/types/dist/partners-portal/partner-city-quest.dto';
import { PartnerCityQuestRO } from '@routellers/types/dist/partners-portal/partner-city-quest.ro';
import {
    CreatePartnerCityReadyRouteDto,
    UpdatePartnerCityReadyRouteDto,
} from '@routellers/types/dist/partners-portal/partner-city-ready-route.dto';
import { PartnerCityReadyRouteRO } from '@routellers/types/dist/partners-portal/partner-city-ready-route.ro';
import { PortalCityOptionRO } from '@routellers/types/dist/partners-portal/portal-city-option.ro';
import { PortalPartnerRO } from '@routellers/types/dist/partners-portal/portal-partner.ro';
import { PortalPartnerStatsRO } from '@routellers/types/dist/partners-portal/portal-stats.ro';
import { RegisterPortalPartnerDto } from '@routellers/types/dist/partners-portal/register-portal-partner.dto';
import { UpdatePartnerAttractionDto } from '@routellers/types/dist/partners-portal/update-partner-attraction.dto';
import { UpdatePortalPartnerDto } from '@routellers/types/dist/partners-portal/update-portal-partner.dto';

import { apiClient } from './client';

type AuthResponse = {
    success: boolean;
    token?: string;
    emailVerified?: boolean;
    partner?: PortalPartnerRO;
};

export type AttractionsQuery = {
    cityId: number;
    searchText?: string;
    category?: AttractionCategoryEnum;
    fame?: AttractionFameEnum;
    district?: string;
};

export type AttractionDuplicatesQuery = {
    cityId: number;
    lat: number;
    lng: number;
    title?: string;
    excludeId?: number;
};

export const api = {
    register: (data: RegisterPortalPartnerDto) =>
        apiClient.post<{ success: boolean }>('/partners-portal/register', data),

    verifyEmail: (data: { email: string; code: string }) =>
        apiClient.post<AuthResponse>('/partners-portal/verify-email', data),

    resendCode: (email: string) =>
        apiClient.post<{ success: boolean }>('/partners-portal/resend-code', { email }),

    login: (data: { email: string; password: string }) =>
        apiClient.post<AuthResponse>('/partners-portal/login', data),

    logout: () => apiClient.get('/partners-portal/logout'),

    recover: (email: string) =>
        apiClient.post<{ success: boolean }>('/partners-portal/recover', { email }),

    resetPassword: (data: { email: string; code: string; password: string }) =>
        apiClient.post<AuthResponse>('/partners-portal/reset-password', data),

    me: () => apiClient.get<PortalPartnerRO>('/partners-portal/me'),

    updateMe: (data: UpdatePortalPartnerDto) =>
        apiClient.patch<PortalPartnerRO>('/partners-portal/me', data),

    uploadImage: (file: File, type: string) => {
        const data = new FormData();

        data.append('file', file);
        data.append('type', type);

        return apiClient.post<{ url: string }>('/uploads/image', data);
    },

    questCities: (search?: string) =>
        apiClient.get<PortalCityOptionRO[]>('/partners-portal/quest-cities', {
            params: search?.trim() ? { search: search.trim() } : undefined,
        }),

    /** @deprecated Use questCities */
    storyCities: (search?: string) =>
        apiClient.get<PortalCityOptionRO[]>('/partners-portal/quest-cities', {
            params: search?.trim() ? { search: search.trim() } : undefined,
        }),

    attractions: (query: AttractionsQuery) =>
        apiClient.get<CityAttractionRO[]>('/partners-portal/attractions', {
            params: {
                cityId: query.cityId,
                searchText: query.searchText || undefined,
                category: query.category || undefined,
                fame: query.fame || undefined,
                district: query.district || undefined,
            },
        }),

    attractionDuplicates: (query: AttractionDuplicatesQuery) =>
        apiClient.get<CityAttractionRO[]>('/partners-portal/attractions/duplicates', {
            params: {
                cityId: query.cityId,
                lat: query.lat,
                lng: query.lng,
                title: query.title || undefined,
                excludeId: query.excludeId || undefined,
            },
        }),

    createAttraction: (data: CreatePartnerAttractionDto) =>
        apiClient.post<CityAttractionRO>('/partners-portal/attractions', data),

    updateAttraction: (id: number, data: UpdatePartnerAttractionDto) =>
        apiClient.patch<CityAttractionRO>(`/partners-portal/attractions/${id}`, data),

    stats: () => apiClient.get<PortalPartnerStatsRO>('/partners-portal/stats'),

    quests: () => apiClient.get<PartnerCityQuestRO[]>('/partners-portal/quests'),

    quest: (id: number) => apiClient.get<PartnerCityQuestRO>(`/partners-portal/quests/${id}`),

    createQuest: (data: CreatePartnerCityQuestDto) =>
        apiClient.post<PartnerCityQuestRO>('/partners-portal/quests', data),

    updateQuest: (id: number, data: UpdatePartnerCityQuestDto) =>
        apiClient.patch<PartnerCityQuestRO>(`/partners-portal/quests/${id}`, data),

    deleteQuest: (id: number) => apiClient.delete(`/partners-portal/quests/${id}`),

    submitQuest: (id: number) =>
        apiClient.post<PartnerCityQuestRO>(`/partners-portal/quests/${id}/submit`),

    routes: () => apiClient.get<PartnerCityReadyRouteRO[]>('/partners-portal/routes'),

    route: (id: number) => apiClient.get<PartnerCityReadyRouteRO>(`/partners-portal/routes/${id}`),

    createRoute: (data: CreatePartnerCityReadyRouteDto) =>
        apiClient.post<PartnerCityReadyRouteRO>('/partners-portal/routes', data),

    updateRoute: (id: number, data: UpdatePartnerCityReadyRouteDto) =>
        apiClient.patch<PartnerCityReadyRouteRO>(`/partners-portal/routes/${id}`, data),

    deleteRoute: (id: number) => apiClient.delete(`/partners-portal/routes/${id}`),

    submitRoute: (id: number) =>
        apiClient.post<PartnerCityReadyRouteRO>(`/partners-portal/routes/${id}/submit`),
};
